import {
  baseUrl,
  scope,
  logBaseUrl
} from '@/environment.js'
import store from '@/store'
// this is for mock data
// import responseJson from '@/assets/mockData/metadataMockResponse.json'

const checkStatus = function (response) {
  if (response.status >= 200 && response.status < 300) {
    return response
  } else {
    const err = new Error(response.statusText)
    err.response = response
    throw err
  }
}

const fetchWithTimeout = async function (url, options = {}) {
  let didTimeOut = false
  return new Promise(function (resolve, reject) {
    let timeout
    if (options.timeout) {
      timeout = setTimeout(function () {
        didTimeOut = true
        reject(new Error('timeout'))
      }, options.timeout)
    }

    fetch(url, options)
      .then(function (response) {
        if (options.timeout) {
          clearTimeout(timeout)
        }
        if (!didTimeOut) {
          resolve(response)
        }
      })
      .catch(function (err) {
        if (didTimeOut) return
        reject(err)
      })
  })
    .then(function (response) {
      return response
    })
    .catch(function (err) {
      return err
    })
}

export const fetchMetadata = () => {
  let url = new URL(`${baseUrl}/metadata/${store.state.slug}`)
  if (store.state.locale.selected !== store.state.locale.default) {
    url = url + `/${store.state.locale.selected}`
  }
  return fetchWithTimeout(url)
    .then((response) => checkStatus(response))
    .then((response) => response.json())
    .then((response) => {
      if (response.message === 'Token not found') {
        throw new Error(response.message)
      } else {
        return response
        // this is for mock data
        // return responseJson
      }
    })
    .catch((e) => { throw e })
}

export const fetchStripePaymentMethods = () => {
  let url = new URL(`${baseUrl}/metadata/${store.state.slug}/paymentMethods`)
  if (store.state.locale.selected !== store.state.locale.default) {
    url = url + `/${store.state.locale.selected}`
  }
  return fetchWithTimeout(url)
    .then((response) => checkStatus(response))
    .then((response) => response.json())
    .then((response) => {
      if (response.message === 'Token not found') {
        throw new Error(response.message)
      } else {
        return response
        // this is for mock data
        // return responseJson
      }
    })
    .catch((e) => { throw e })
}

export const fetchToken = async () => {
  const url = new URL(`${baseUrl}/oauth/token`)
  const body = new URLSearchParams({
    grant_type: 'client_credentials',
    client_id: store.state.slug,
    client_secret: store.state.code.toLowerCase(),
    scope
  })
  const headers = new Headers({
    'accept': '*/*',
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
  })
  return fetchWithTimeout(url, {
    method: 'POST',
    body,
    headers
  })
    .then((response) => checkStatus(response))
    .then((response) => response.json())
    .then((response) => (response))
    .catch((e) => { throw e })
}

export const fetchServices = async () => {
  const url = new URL(`${baseUrl}/services/${store.state.slug}/${store.state.locale.selected}`)
  const headers = new Headers({
    Authorization: `Bearer ${store.state.token}`
  })
  return fetchWithTimeout(url, {
    headers
  })
    .then((response) => checkStatus(response))
    .then((response) => response.json())
    .then((response) => (response))
    .catch((e) => { throw e })
}

export const fetchInspectionReportUrl = async () => {
  const url = new URL(`${baseUrl}/inspection/${store.state.slug}/${store.state.locale.selected}`)
  const headers = new Headers({
    Authorization: `Bearer ${store.state.token}`
  })
  return fetchWithTimeout(url, {
    headers
  })
    .then((response) => checkStatus(response))
    .then((response) => response.json())
    .then((response) => (response))
    .catch((e) => { throw e })
}

export const fetchAdvisor = async () => {
  const url = new URL(`${baseUrl}/confirmation/${store.state.slug}`)
  const headers = new Headers({
    Authorization: `Bearer ${store.state.token}`
  })
  return fetchWithTimeout(url, {
    headers
  })
    .then((response) => checkStatus(response))
    .then((response) => response.json())
    .then((response) => (response))
    .catch((e) => { throw e })
}

export const fetchTax = async (serviceIds) => {
  const url = new URL(`${baseUrl}/tax/${store.state.slug}`)
  const headers = new Headers({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${store.state.token}`
  })
  const body = JSON.stringify({
    selectedServices: serviceIds
  })

  return fetchWithTimeout(url, {
    method: 'POST',
    headers,
    body
  })
    .then((response) => checkStatus(response))
    .then((response) => response.json())
    .then((response) => (response))
    .catch((e) => { throw e })
}

export const putMediaTracking = async (mediaId) => {
  const url = new URL(
    `${baseUrl}/tracking/${store.state.slug}/media/${mediaId}`,
  );
  const headers = new Headers({
    Authorization: `Bearer ${store.state.token}`,
    accept: "*/*",
  });
  return fetchWithTimeout(url, {
    method: "PUT",
    headers,
  })
    .then((response) => checkStatus(response))
    .catch((e) => {
      console.error("Error putting media view tracking to Api:", e);
    });
}

export const postServices = async () => {
  const confirmedServices = []
  
  // Helper function to process child sublets
  const processSubletServices = (parentService) => {
    if (parentService.subletServices && parentService.subletServices.length > 0) {
      parentService.subletServices.forEach((sublet) => {
        if (sublet.isSelected) {
          sublet.isApproved = true
          sublet.reasonId = -1
          confirmedServices.push(sublet)
        } else {
          sublet.isApproved = false
          sublet.reasonId = typeof sublet.reasonId === 'undefined' ? sublet.declinedReasonId : sublet.reasonId
          confirmedServices.push(sublet)
        }
      })
    }
  }
  
  store.state.services.forEach((service) => {
    if (
      service.category !== '3' &&
      service.category !== '4' &&
      service.category !== '8' &&
      service.category !== '9'
    ) {
      if (service.subServices) {
        service.subServices.forEach((subService) => {
          if (subService.isSelected) {
            subService.isApproved = true
            subService.reasonId = -1
            confirmedServices.push(subService)
          } else {
            subService.isApproved = false
            subService.reasonId = typeof subService.reasonId === 'undefined' ? subService.declinedReasonId : subService.reasonId
            confirmedServices.push(subService)
          }
          // Process child sublets for this subService
          processSubletServices(subService)
        })
        // Also process child sublets attached directly to the parent service
        processSubletServices(service)
      } else if (service.category !== '6' && service.category !== '7') {
        if (service.isSelected) {
          service.isApproved = true
          service.reasonId = -1
          confirmedServices.push(service)
        } else {
          service.isApproved = false
          service.reasonId = typeof service.reasonId === 'undefined' ? service.declinedReasonId : service.reasonId
          confirmedServices.push(service)
        }
        // Process child sublets for this service
        processSubletServices(service)
      }
    }
  })
  // Remove duplicate services by ID (standalone sublets may have duplicates)
  const uniqueServices = []
  const seenIds = new Set()
  for (const service of confirmedServices) {
    if (!seenIds.has(service.id)) {
      seenIds.add(service.id)
      uniqueServices.push(service)
    }
  }
  
  // Filter out child sublet services (those with parentServiceLineIdForSublet non-null)
  const filteredServices = uniqueServices.filter(service => 
    service.parentServiceLineIdForSublet === null || 
    service.parentServiceLineIdForSublet === undefined
  )
  
  const payload = filteredServices.map(service => {
    return {
      category: service.category,
      comment: service.comment,
      declinedReasonId: service.declinedReasonId,
      iconUrl: service.iconUrl,
      id: service.id,
      imageUrl: service.imageUrl,
      isApproved: service.isApproved,
      isHighlighted: service.isHighlighted,
      isSelected: service.isSelected,
      laborMatrixPayment: service.laborMatrixPayment,
      laborMatrixPaymentTranslations: service.laborMatrixPaymentTranslations,
      name: service.name,
      parentServiceId: service.parentServiceId,
      price: service.price,
      reasonId: service.reasonId,
      serviceAdvisorComments: service.serviceAdvisorComments,
      taxAndFee: service.taxAndFee,
      // Sublet-specific fields
      isStandAloneSublet: service.isStandAloneSublet,
      parentServiceLineIdForSublet: service.parentServiceLineIdForSublet
    }
  })

  const url = new URL(`${baseUrl}/services/${store.state.slug}${store.state.route.query.src ? '?src=' + store.state.route.query.src : ( window.location.search ? window.location.search : '') }`)
  const headers = new Headers({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${store.state.token}`,
    'accept': '*/*'
  })
  const body = JSON.stringify({
    confirmedServices: payload,
    customerSignature: store.state.signature
  })

  return fetchWithTimeout(url, {
    method: 'POST',
    headers,
    body
  })
    .then((response) => checkStatus(response))
    .then((response) => response.json())
    .then((response) => (response))
    .catch((e) => { throw e })
}

export const postLog = async () => {
  const url = new URL(`${logBaseUrl}`)
  const headers = new Headers({
    'Content-Type': 'application/json'
  })
  const body = JSON.stringify(store.state.log)

  return fetchWithTimeout(url, {
    method: 'POST',
    headers,
    body
  })
    .then((response) => checkStatus(response))
    .then((response) => response.json())
    .then((response) => (response))
    .catch((e) => { throw e })
}

export default {
  fetchMetadata,
  fetchToken
}
